import { fetchEvents, fetchProducts } from "../lib/api.js";
import { formatCurrency, formatDate, toLocalISODate } from "../lib/format.js";
import { collectionOf } from "../lib/calc.js";
import { sum } from "../lib/aggregate.js";
import { escapeHtml, showError } from "../lib/ui.js";

export async function renderDashboard(container) {
  container.innerHTML = `<h2>Panel general</h2><p>Cargando…</p>`;

  try {
    const [events, products] = await Promise.all([fetchEvents(), fetchProducts()]);

    const now = new Date();
    const today = toLocalISODate(now);
    // ---- Cuatro números sobre todo lo vendido ----
    //
    // Se explican entre sí, sin restar nada por atrás:
    //   Caja + A cobrar = lo vendido
    //   lo vendido − Costos = Ganancia
    //
    // "Caja" es lo que ya entró: señas, y los saldos cuando se cobren. NO se le
    // restan los gastos — esos van en su propia tarjeta. Antes decía "cobrado
    // menos gastado" y no se entendía, sobre todo con todos los eventos todavía
    // por realizar, donde lo gastado es cero.
    // Misma regla que la ficha y la lista (collectionOf): el saldo se da por
    // cobrado el día del cumple salvo que esté marcado como pendiente.
    const cobranzas = events.map((e) => collectionOf(e, today));
    const vendido = sum(cobranzas, (c) => c.price);
    const caja = sum(cobranzas, (c) => c.collected);
    const aCobrar = sum(cobranzas, (c) => c.pending);
    const costos = sum(events, (e) => Number(e.cost_total) || 0);
    const ganancia = vendido - costos;

    const realizados = events.filter((e) => e.date <= today).length;
    const porRealizar = events.length - realizados;

    const upcoming = events.filter((e) => e.date >= today).sort((a, b) => (a.date > b.date ? 1 : -1));

    const productStats = products
      .map((p) => {
        const productEvents = events.filter((e) => e.product_id === p.id);
        return {
          ...p,
          count: productEvents.length,
          income: sum(productEvents, (e) => e.income),
          profit: sum(productEvents, (e) => e.profit),
        };
      })
      .filter((p) => p.count > 0)
      .sort((a, b) => b.profit - a.profit);

    const maxProfit = Math.max(1, ...productStats.map((p) => Math.abs(p.profit)));

    container.innerHTML = `
      <h2>Panel general</h2>

      <div class="cards-grid">
        <div class="card">
          <div class="label">Vendido</div>
          <div class="value">${formatCurrency(vendido)}</div>
          <div class="label" style="margin-top:6px">caja más a cobrar</div>
        </div>
        <div class="card">
          <div class="label">Caja</div>
          <div class="value positive">${formatCurrency(caja)}</div>
          <div class="label" style="margin-top:6px">señas y saldos ya cobrados</div>
        </div>
        <div class="card">
          <div class="label">A cobrar</div>
          <div class="value ${aCobrar > 0 ? "negative" : ""}">${formatCurrency(aCobrar)}</div>
          <div class="label" style="margin-top:6px">falta cobrar de lo vendido</div>
        </div>
        <div class="card">
          <div class="label">Costos</div>
          <div class="value">${formatCurrency(costos)}</div>
          <div class="label" style="margin-top:6px">de todo lo vendido</div>
        </div>
        <div class="card">
          <div class="label">Ganancia</div>
          <div class="value ${ganancia >= 0 ? "positive" : "negative"}">${formatCurrency(ganancia)}</div>
          <div class="label" style="margin-top:6px">lo vendido menos los costos</div>
        </div>
      </div>
      <p class="hint" style="margin:-8px 0 20px">
        ${events.length} cumpleaños vendidos · ${realizados} realizados · ${porRealizar} por realizar
      </p>

      <div class="panel">
        <h3>Rentabilidad por producto</h3>
        ${
          productStats.length === 0
            ? "<p>Todavía no hay eventos cargados.</p>"
            : productStats
                .map(
                  (p) => `
          <div class="product-bar-row">
            <div class="meta">
              <span>${escapeHtml(p.name)} <span style="color: var(--text-muted)">· ${p.count} evento(s)</span></span>
              <strong class="${p.profit >= 0 ? "value positive" : "value negative"}" style="font-size: 14px">${formatCurrency(p.profit)}</strong>
            </div>
            <div class="product-bar-track">
              <div class="product-bar-fill" style="width: ${Math.round((Math.abs(p.profit) / maxProfit) * 100)}%; background: ${escapeHtml(p.profit >= 0 ? p.color : "#e0554c")}"></div>
            </div>
          </div>`
                )
                .join("")
        }
      </div>

      <div class="panel">
        <h3>Próximos eventos</h3>
        ${
          upcoming.length === 0
            ? "<p>No hay eventos próximos agendados.</p>"
            : `<div class="table-wrap"><table class="table-cards">
                <thead><tr><th>Fecha</th><th>Evento</th><th>Producto</th><th>Chicos</th><th>Ingreso</th><th>Ganancia</th></tr></thead>
                <tbody>
                  ${upcoming
                    .slice(0, 10)
                    .map(
                      (e) => `<tr>
                        <td data-label="Fecha">${formatDate(e.date)}</td>
                        <td data-label="Evento">${escapeHtml(e.description)}</td>
                        <td data-label="Producto">${escapeHtml(products.find((p) => p.id === e.product_id)?.name ?? "—")}</td>
                        <td data-label="Chicos">${e.children_count}</td>
                        <td data-label="Ingreso">${formatCurrency(e.income)}</td>
                        <td data-label="Ganancia" class="${e.profit >= 0 ? "value positive" : "value negative"}" style="font-size: 14px">${formatCurrency(e.profit)}</td>
                      </tr>`
                    )
                    .join("")}
                </tbody>
              </table></div>`
        }
      </div>
    `;

    // Bloque nuevo de Colonia y Empresa (tanda 2), debajo de todo y con
    // consultas propias. Con el interruptor apagado no agrega nada.
    import("./colonia/bloques.js").then((m) => m.mountPanelBloque(container)).catch(() => {});
  } catch (error) {
    showError(container, error);
  }
}
